export type SetupKind = 'cli' | 'config_file'
export type SetupScope = 'global' | 'project'

/**
 * One way to configure a client tool to use a Hawk repo as its index/registry.
 * Mirrors `crate::ecosystem::ClientSetup`.
 *
 * Snippets may contain these placeholders, all rewritten by `resolveSnippet`:
 * - `{base}`            -> repo's mount URL (e.g. `https://hawk/repo/org/pypi`)
 * - `{base_no_scheme}`  -> same URL minus the `https://` (npm's `//host/path` form)
 * - `{base_with_token}` -> same URL with `__token__:TOKEN@` embedded as creds
 * - `{host}`            -> just the hostname (no scheme, no port, no path) - for ~/.netrc
 * - `{token}`           -> the raw PAT (or `YOUR_TOKEN` placeholder)
 */
export interface ClientSetup {
  client: string
  label: string
  kind: SetupKind
  scope: SetupScope
  file_path: string | null
  snippet: string
  auth: AuthStep | null
}

/** Separate authentication block rendered below a `ClientSetup`'s main snippet. */
export interface AuthStep {
  label: string
  kind: SetupKind
  file_path: string | null
  snippet: string
}

export const TOKEN_PLACEHOLDER = 'YOUR_TOKEN'

/** Rewrite all known placeholders in a snippet. */
export function resolveSnippet(
  snippet: string,
  base: string,
  token: string | null,
): string {
  const effectiveToken = token && token.trim() ? token.trim() : TOKEN_PLACEHOLDER
  return snippet
    .replaceAll('{base_with_token}', embedTokenInUrl(base, effectiveToken))
    .replaceAll('{base_no_scheme}', stripScheme(base))
    .replaceAll('{base}', base)
    .replaceAll('{host}', hostOnly(base))
    .replaceAll('{token}', effectiveToken)
}

function stripScheme(base: string): string {
  const m = base.match(/^[a-z][a-z0-9+\-.]*:\/\//i)
  return m ? base.slice(m[0].length) : base
}

/** Just the hostname (no scheme, no port, no path) - used for ~/.netrc. */
function hostOnly(base: string): string {
  try {
    return new URL(base).hostname
  } catch {
    return base
  }
}

/**
 * Return `base` with HTTP Basic credentials (user `__token__`, password = the
 * given token) embedded. Falls back to the original string if `base` is not a
 * parseable URL. Uses manual splice so the literal `YOUR_TOKEN` placeholder
 * stays readable instead of percent-encoded.
 */
function embedTokenInUrl(base: string, token: string): string {
  const schemeMatch = base.match(/^([a-z][a-z0-9+\-.]*):\/\//i)
  if (!schemeMatch) return base
  const scheme = schemeMatch[1]
  const rest = base.slice(schemeMatch[0].length)
  return `${scheme}://__token__:${token}@${rest}`
}
